import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from '../components';
import usageService from '../api/services/usageService';

// --- Helpers ---

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
};

const formatCurrency = (amount) => {
  if (amount == null) return '$0.00';
  return `$${Number(amount).toFixed(2)}`;
};

const getBarColor = (pct) => {
  if (pct > 95) return '#ef4444';
  if (pct > 80) return '#f97316';
  if (pct > 60) return '#f59e0b';
  return '#22c55e';
};

const STATUS_BADGE = {
  Active:      { color: '#22c55e', bg: '#22c55e20' },
  Trial:       { color: '#3b82f6', bg: '#3b82f620' },
  Expired:     { color: '#ef4444', bg: '#ef444420' },
  Suspended:   { color: '#f59e0b', bg: '#f59e0b20' },
  Cancelled:   { color: '#6b7280', bg: '#6b728020' },
  GracePeriod: { color: '#f97316', bg: '#f9731620' },
};

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${p => p.bg || '#22c55e20'};
  color: ${p => p.color || '#22c55e'};
`;

const Card = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const CardTitle = styled.h3`
  margin: 0 0 ${p => p.theme.spacing.lg} 0;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
`;

const LimitRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.md};
  &:last-child { margin-bottom: 0; }
`;

const LimitLabel = styled.div`
  width: 130px;
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const ProgressBarOuter = styled.div`
  flex: 1;
  height: 10px;
  background-color: ${p => p.theme.colors.border};
  border-radius: 5px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  border-radius: 5px;
  width: ${p => Math.min(p.pct, 100)}%;
  background-color: ${p => p.barColor};
  transition: width 0.4s ease;
`;

const LimitValue = styled.div`
  width: 160px;
  flex-shrink: 0;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.textColor || p.theme.colors.textSecondary};
`;

const UnlimitedLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const InfoCard = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
`;

const InfoValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${p => p.valueColor || p.theme.colors.text};
  margin-bottom: 4px;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl} 0;
`;

// --- Component ---

export const LicenseStatusSection = () => {
  const theme = useTheme();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const usageRes = await usageService.getMyUsage();
      const usageData = usageRes?.data || usageRes;
      setUsage(usageData);
    } catch (err) {
      console.error('Failed to fetch license status:', err);
      toast.error('Failed to load license status');
    } finally {
      setLoading(false);
    }
  };

  // --- Limit rendering ---

  const renderLimit = (label, used, limit, isStorage = false) => {
    const usedVal = used ?? 0;
    const isUnlimited = limit == null || limit === 0;

    if (isUnlimited) {
      const displayUsed = isStorage ? formatBytes(usedVal) : usedVal.toLocaleString();
      return (
        <LimitRow theme={theme} key={label}>
          <LimitLabel theme={theme}>{label}</LimitLabel>
          <div style={{ flex: 1 }}>
            <UnlimitedLabel theme={theme}>Unlimited</UnlimitedLabel>
          </div>
          <LimitValue theme={theme}>{displayUsed} used</LimitValue>
        </LimitRow>
      );
    }

    const pct = Math.round((usedVal / limit) * 100);
    const barColor = getBarColor(pct);
    const displayUsed = isStorage ? formatBytes(usedVal) : usedVal.toLocaleString();
    const displayLimit = isStorage ? formatBytes(limit) : limit.toLocaleString();

    return (
      <LimitRow theme={theme} key={label}>
        <LimitLabel theme={theme}>{label}</LimitLabel>
        <ProgressBarOuter theme={theme}>
          <ProgressBarInner pct={pct} barColor={barColor} />
        </ProgressBarOuter>
        <LimitValue theme={theme} textColor={barColor}>
          {displayUsed} of {displayLimit} ({pct}%)
        </LimitValue>
      </LimitRow>
    );
  };

  // --- Expiry info ---

  const expiryDate = usage?.expiryDate;
  const daysLeft = usage?.daysUntilExpiry ?? daysUntil(expiryDate);
  const expiryColor = daysLeft != null
    ? (daysLeft <= 7 ? '#ef4444' : daysLeft <= 30 ? '#f97316' : '#22c55e')
    : undefined;

  // --- Status info ---

  const planName = usage?.planName || 'N/A';
  const status = usage?.licenseStatus || 'Active';
  const statusStyle = STATUS_BADGE[status] || STATUS_BADGE.Active;

  // --- Overage info ---

  const overageCost = usage?.totalOverageCostThisMonth ?? 0;

  return (
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <HeaderRow theme={theme}>
            <ThemedText type="title">License Status</ThemedText>
            <StatusBadge bg={statusStyle.bg} color={statusStyle.color}>
              {status}
            </StatusBadge>
          </HeaderRow>
          <ThemedText type="subtitle">
            Plan: {planName}
          </ThemedText>
        </PageHeader>

        {loading ? (
          <LoadingState theme={theme}>
            <ThemedText>Loading license status...</ThemedText>
          </LoadingState>
        ) : !usage ? (
          <Card theme={theme}>
            <ThemedText>No license information available.</ThemedText>
          </Card>
        ) : (
          <>
            {/* Resource Limits */}
            <Card theme={theme}>
              <CardTitle theme={theme}>Resource Usage</CardTitle>
              {renderLimit(
                'Users',
                usage.users?.current,
                usage.users?.limit
              )}
              {renderLimit(
                'Forms',
                usage.formSchemas?.current,
                usage.formSchemas?.limit
              )}
              {renderLimit(
                'Submissions',
                usage.submissionsThisMonth?.current,
                usage.submissionsThisMonth?.limit
              )}
              {usage.totalSubmissions && usage.totalSubmissions.limit != null && renderLimit(
                'Total Submissions',
                usage.totalSubmissions?.current,
                usage.totalSubmissions?.limit
              )}
              {renderLimit(
                'Storage',
                usage.storage?.currentBytes,
                usage.storage?.limitBytes,
                true
              )}
              {renderLimit(
                'Report Templates',
                usage.pdfTemplates?.current,
                usage.pdfTemplates?.limit
              )}
            </Card>

            {/* Expiry & Overage summary cards */}
            <InfoGrid theme={theme}>
              <InfoCard theme={theme}>
                <InfoLabel theme={theme}>License Expiry</InfoLabel>
                <InfoValue theme={theme} valueColor={expiryColor}>
                  {expiryDate ? fmtDate(expiryDate) : 'No Expiry'}
                </InfoValue>
                {daysLeft != null && (
                  <InfoLabel theme={theme}>
                    {daysLeft > 0
                      ? `${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`
                      : daysLeft === 0
                        ? 'Expires today'
                        : `Expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft) !== 1 ? 's' : ''} ago`}
                  </InfoLabel>
                )}
              </InfoCard>

              <InfoCard theme={theme}>
                <InfoLabel theme={theme}>Overage Charges This Month</InfoLabel>
                <InfoValue theme={theme} valueColor={overageCost > 0 ? '#f97316' : undefined}>
                  {formatCurrency(overageCost)}
                </InfoValue>
                {overageCost > 0 && (
                  <InfoLabel theme={theme}>
                    Charges for usage above plan limits
                  </InfoLabel>
                )}
              </InfoCard>
            </InfoGrid>
          </>
        )}
      </PageContainer>
  );
};

export const LicenseStatusPage = () => (
  <Layout currentPath="/settings">
    <LicenseStatusSection />
  </Layout>
);

export default LicenseStatusPage;
